/**
 * Strategy settings of a symbol that has its OWN settings document (the Vest
 * chart feed, e.g. BTC-PERP.VEST, #622). Shown in Settings > Strategy when
 * the active chart is such a symbol, in place of the CME tabs, so an edit on
 * a BTC chart can never land on NQ's settings.
 *
 * Reads `GET /api/symbol-settings/{symbol}`; each strategy section saves on
 * its own via `POST /api/symbol-settings/{symbol}/{section}` (the engine
 * persists `strategy:settings:symbol:{SYMBOL}` and applies it live). A
 * section that is absent is idle for the symbol (never NQ's values).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CONFIG } from '../config';
import { useSymbolChartDisplayStore } from '../store/symbolChartDisplayStore';
import { describeSection, editableFields, setField, type FieldValue } from '../utils/symbolSettingsForm';

interface SymbolSettingsDoc {
  symbol: string;
  key: string;
  loaded: boolean;
  hasDefaultsFile: boolean;
  enabled: string[];
  sections: string[];
  settings: Record<string, unknown> | null;
}

interface Props {
  symbol: string;
}

const SymbolSettingsPanel: React.FC<Props> = ({ symbol }) => {
  const [doc, setDoc] = useState<SymbolSettingsDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Record<string, unknown>>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const base = CONFIG.getBackendUrl(window.location.hostname);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${base}/api/symbol-settings/${encodeURIComponent(symbol)}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setDoc(body as SymbolSettingsDoc);
      setDrafts({});
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [base, symbol]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (section: string) => {
    const draft = drafts[section];
    if (!draft) return;
    setSaving(section);
    try {
      const res = await fetch(`${base}/api/symbol-settings/${encodeURIComponent(symbol)}/${section}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const body = await res.json();
      if (!res.ok || body?.success === false) throw new Error(body?.error || `HTTP ${res.status}`);
      await load();
      // an open chart of this symbol picks the new chartDisplay up now
      void useSymbolChartDisplayStore.getState().refresh(symbol);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  };

  const sections = (doc?.sections ?? []).filter((s) => s !== 'srOverrides');
  const settings = doc?.settings ?? null;

  return (
    <div className="gs-tab-content" data-testid="symbol-settings-panel">
      <div className="gs-section">
        <div className="gs-section-header">
          <span className="gs-section-icon">₿</span>
          <span className="gs-section-title">{symbol} strategy settings</span>
        </div>
        <p className="gs-section-description">
          This chart has its own settings, separate from NQ/MNQ/ES/MES. Signals here are chart-only: no bot and no order ever
          trades a Vest chart feed symbol.
          {settings && typeof settings._provenance === 'string' ? ` Source: ${settings._provenance}.` : ''}
        </p>
        {error && <p className="gs-sync-error">{error}</p>}
        {doc && !doc.loaded && (
          <p className="gs-label-hint">
            Not loaded: {doc.hasDefaultsFile ? 'the engine has not read its settings yet (Redis unavailable?)' : 'no defaults file exists for this symbol'}. Strategies stay idle.
          </p>
        )}
      </div>

      {settings &&
        sections.map((section) => {
          const current = (drafts[section] ?? (settings[section] as Record<string, unknown> | undefined)) || null;
          const desc = describeSection(section);
          const isOpen = open === section;
          const enabled = current ? current.enabled === true : false;
          return (
            <div className="gs-section" key={section} data-testid={`symbol-settings-${section}`}>
              <div className="gs-section-header" onClick={() => setOpen(isOpen ? null : section)} style={{ cursor: 'pointer' }}>
                <span className="gs-section-icon">{isOpen ? '▾' : '▸'}</span>
                <span className="gs-section-title">{desc.title}</span>
                <span className="gs-label-hint" style={{ marginLeft: 'auto' }}>
                  {current ? (enabled ? 'on' : 'off') : 'idle (no section)'}
                </span>
              </div>
              {isOpen && current && (
                <>
                  {editableFields(current).map(({ path, value }) => (
                    <div className="gs-row" key={path}>
                      <div className="gs-label">
                        <span className="gs-label-main">{path}</span>
                      </div>
                      <FieldInput
                        value={value}
                        onChange={(v) => setDrafts((d) => ({ ...d, [section]: setField(current, path, v) }))}
                      />
                    </div>
                  ))}
                  <div className="gs-row">
                    <button
                      className={`gs-btn gs-btn-save ${drafts[section] ? 'has-changes' : ''}`}
                      disabled={!drafts[section] || saving === section}
                      onClick={() => save(section)}
                    >
                      {saving === section ? 'Saving...' : `Save ${desc.title}`}
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
    </div>
  );
};

const FieldInput: React.FC<{ value: FieldValue; onChange: (v: FieldValue) => void }> = ({ value, onChange }) => {
  if (typeof value === 'boolean') {
    return (
      <label className="gs-toggle-switch">
        <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
        <span className="gs-toggle-slider" />
      </label>
    );
  }
  if (typeof value === 'number') {
    return (
      <input
        className="gs-input"
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
      />
    );
  }
  if (typeof value === 'string') {
    return <input className="gs-input" type="text" value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  // arrays / objects / null: JSON, applied when it parses
  return <JsonInput value={value} onChange={onChange} />;
};

const JsonInput: React.FC<{ value: FieldValue; onChange: (v: FieldValue) => void }> = ({ value, onChange }) => {
  const [text, setText] = useState(JSON.stringify(value));
  const [bad, setBad] = useState(false);
  return (
    <input
      className="gs-input"
      type="text"
      value={text}
      style={bad ? { borderColor: 'var(--danger, #ef4444)' } : undefined}
      onChange={(e) => {
        setText(e.target.value);
        try {
          onChange(JSON.parse(e.target.value) as FieldValue);
          setBad(false);
        } catch {
          setBad(true);
        }
      }}
    />
  );
};

export default SymbolSettingsPanel;
